// PRE RMA — Gestion des intrants du PEV (vaccins, seringues, diluants).
// Source : section « Gestion des intrants » de pre_rma_pev.html (mêmes produits, mêmes règles, mêmes seuils).
// Fonctions pures : mêmes résultats sur l'écran (hors-ligne), l'API et les exports.
//
//   Stock fin calculé = Stock début + Qt reçue − Total utilisé − Qt périmée − Qt avariée
//   Total utilisé     = somme des quantités saisies par séance de vaccination
//   Stock fin         = valeur saisie (comptage physique) sinon valeur calculée ; « Contrôle » = Erreur si saisie ≠ calculée
//   Taux de perte     = (Total utilisé − enfants vaccinés) / Total utilisé × 100 (produits ayant des antigènes) ; alerte ≥ 10 %
//   Multiples         = alerte NON bloquante si une quantité n'est pas un multiple du conditionnement (flacon)
//   Report            = le stock fin du mois N devient le stock début du mois N+1 (si non saisi)
import { totauxParAntigene } from './pev.ts';
import type { PevAntigene, PevSeance } from './pev.ts';

export interface DefinitionIntrantPev {
  key: string;
  label: string;
  /** Antigènes de la fiche de pointage dont la somme sert de « repère » et de base au taux de perte (null = aucun). */
  antigenes: PevAntigene[] | null;
  /** Conditionnement : nombre de doses par flacon / boîte (null = pas de contrôle). */
  multiple: number | null;
  /**
   * Ligne du bloc « Programme : PEV » du Tableau 18 du modèle officiel (feuille T18_Stock_Intrants, lignes 6 à 24) — null = aucune
   * correspondance CERTAINE : le produit n'est alors PAS écrit dans T18 (il reste dans l'annexe d'export), rien n'est deviné.
   */
  t18Ligne: number | null;
  /** Désignation exacte de cette ligne dans le modèle officiel (null si t18Ligne est null). */
  t18Libelle: string | null;
}

/**
 * Liste alignée sur le bloc « Programme : PEV (en dose pour les vaccins) » du Tableau 18 officiel, dans l'ordre du modèle.
 * - 12 produits existaient déjà (clés inchangées : les saisies enregistrées restent valables) ; leur libellé d'écran est conservé.
 * - 7 lignes du modèle manquaient et sont ajoutées avec la désignation officielle : kitrougeole, kitpfa, hpv, boitesecu, solvbcg, solvvar, petrole.
 *   Pour ces 7 produits, AUCUN antigène de la fiche de pointage ni conditionnement n'est supposé (antigenes / multiple = null) : pas de repère,
 *   pas de taux de perte, pas de contrôle de multiple — ces règles ne figurent pas dans le modèle officiel.
 * - « SD / Diluant 2 ml » (dil2) et « SD / Diluant 5 ml » (dil5) ne sont PAS rattachés aux lignes « Seringue 2ml » / « Seringue 5ml » du modèle :
 *   l'identité des deux produits n'est pas établie par les fichiers du projet. À confirmer avant de renseigner t18Ligne (19 et 20).
 */
export const PEV_INTRANTS: DefinitionIntrantPev[] = [
  { key: 'bcg', label: 'BCG', antigenes: ['bcg'], multiple: 20, t18Ligne: 6, t18Libelle: 'BCG' },
  { key: 'vpo', label: 'VPO', antigenes: ['vpo0', 'vpo1', 'vpo2', 'vpo3'], multiple: 20, t18Ligne: 7, t18Libelle: 'VPO' },
  { key: 'vpi', label: 'VPI', antigenes: ['vpo3', 'var1'], multiple: 5, t18Ligne: 8, t18Libelle: 'VPI' },
  { key: 'dtc', label: 'DTC', antigenes: ['vpo1', 'vpo2', 'vpo3'], multiple: 10, t18Ligne: 9, t18Libelle: 'DTC HépBHib' },
  { key: 'pcv10', label: 'PCV10', antigenes: ['vpo1', 'vpo2', 'vpo3'], multiple: 4, t18Ligne: 10, t18Libelle: 'PCV 10' },
  { key: 'rota', label: 'ROTA', antigenes: ['vpo1', 'vpo2'], multiple: 1, t18Ligne: 11, t18Libelle: 'Rotavirus' },
  { key: 'var', label: 'VAR', antigenes: ['var1', 'var2'], multiple: 10, t18Ligne: 12, t18Libelle: 'VAR/RR' },
  { key: 'vat', label: 'VAT (Td)', antigenes: null, multiple: 10, t18Ligne: 13, t18Libelle: 'VAT/TD' },
  { key: 'kitrougeole', label: 'Kit Rougeole', antigenes: null, multiple: null, t18Ligne: 14, t18Libelle: 'Kit Rougeole' },
  { key: 'kitpfa', label: 'Kit PFA', antigenes: null, multiple: null, t18Ligne: 15, t18Libelle: 'Kit PFA' },
  { key: 'hpv', label: 'HPV', antigenes: null, multiple: null, t18Ligne: 16, t18Libelle: 'HPV' },
  { key: 'sab05', label: 'SAB 0,5 ml', antigenes: null, multiple: null, t18Ligne: 17, t18Libelle: 'SAB 0,5ml' },
  { key: 'sab005', label: 'SAB 0,05 ml', antigenes: null, multiple: null, t18Ligne: 18, t18Libelle: 'SAB 0,05ml' },
  { key: 'dil2', label: 'SD / Diluant 2 ml', antigenes: null, multiple: null, t18Ligne: null, t18Libelle: null },
  { key: 'dil5', label: 'SD / Diluant 5 ml', antigenes: null, multiple: null, t18Ligne: null, t18Libelle: null },
  { key: 'boitesecu', label: 'Boîte de sécurité', antigenes: null, multiple: null, t18Ligne: 21, t18Libelle: 'Boîte de sécurité' },
  { key: 'solvbcg', label: 'Solvant BCG (flacon)', antigenes: null, multiple: null, t18Ligne: 22, t18Libelle: 'Solvant BCG (flacon)' },
  { key: 'solvvar', label: 'Solvant VAR (flacon)', antigenes: null, multiple: null, t18Ligne: 23, t18Libelle: 'Solvant VAR (flacon)' },
  { key: 'petrole', label: 'Pétrole (litre)', antigenes: null, multiple: null, t18Ligne: 24, t18Libelle: 'Pétrole (litre)' },
];

export const SEUIL_PERTE_ALERTE = 10; // % — au-delà ou égal : alerte visuelle (seuil du fichier d'origine)
export const MAX_SEANCES_INTRANT = 60;
export const estCleIntrantPev = (cle: string) => PEV_INTRANTS.some((d) => d.key === cle);

export interface IntrantPev {
  debut: number | null;
  recu: number | null;
  perime: number | null;
  avarie: number | null;
  /** Stock fin compté/saisi (remplace le calcul s'il est renseigné). */
  finSaisie: number | null;
  /** Quantité utilisée par séance, indexée par la clé (uuid) de la séance PEV — robuste à la suppression d'une séance. */
  bySeance: Record<string, number>;
}

export const intrantPevVide = (): IntrantPev => ({ debut: null, recu: null, perime: null, avarie: null, finSaisie: null, bySeance: {} });

const n = (x: unknown): number => { const v = typeof x === 'number' ? x : parseInt(String(x ?? ''), 10); return Number.isFinite(v) && v > 0 ? v : 0; };

export interface SeanceCle { cle: string; donnees: PevSeance }

/** Repère d'une séance : doses de la fiche de pointage correspondant au produit (null si le produit n'a pas d'antigène). */
export function repereSeance(def: DefinitionIntrantPev, s: PevSeance): number | null {
  return def.antigenes ? def.antigenes.reduce((a, k) => a + n(s.doses[k]), 0) : null;
}

/** Total utilisé : somme des séances EXISTANTES (une séance supprimée ne compte plus, même si sa saisie traîne). */
export function totalUtilise(saisie: Partial<IntrantPev> | null | undefined, seances: SeanceCle[]): number {
  const b = saisie?.bySeance ?? {};
  return seances.reduce((a, s) => a + n(b[s.cle]), 0);
}

export interface CalculIntrantPev {
  debut: number; debutReporte: boolean; recu: number; totalUtilise: number; perime: number; avarie: number;
  finCalcule: number; fin: number; finSaisi: boolean;
  /** « OK » si le stock fin saisi est cohérent avec le calcul (ou non saisi), sinon « Erreur ». */
  coherent: boolean;
  /** Stock fin calculé négatif : sorties supérieures au stock disponible. */
  negatif: boolean;
  vaccines: number | null;
  tauxPerte: number | null;
  alertePerte: boolean;
  /** Champs dont la quantité n'est pas un multiple du conditionnement (alerte non bloquante). */
  multiplesNonRespectes: Array<'debut' | 'recu' | 'totalUtilise' | 'fin'>;
}

export function calculerIntrantPev(
  def: DefinitionIntrantPev, saisie: Partial<IntrantPev> | null | undefined, seances: SeanceCle[], debutReport?: number | null,
): CalculIntrantPev {
  const s = saisie ?? {};
  const reporte = (s.debut === null || s.debut === undefined) && debutReport !== null && debutReport !== undefined;
  const debut = reporte ? n(debutReport) : n(s.debut);
  const total = totalUtilise(s, seances);
  const finCalcule = debut + n(s.recu) - total - n(s.perime) - n(s.avarie);
  const finSaisi = s.finSaisie !== null && s.finSaisie !== undefined;
  const fin = finSaisi ? n(s.finSaisie) : finCalcule;
  const tousSeances = seances.map((x) => x.donnees);
  const t = totauxParAntigene(tousSeances);
  const vaccines = def.antigenes ? def.antigenes.reduce((a, k) => a + t[k], 0) : null;
  const tauxPerte = vaccines !== null && total > 0 ? Math.round(((total - vaccines) / total) * 1000) / 10 : null;
  const m = def.multiple;
  const aVerifier: Array<['debut' | 'recu' | 'totalUtilise' | 'fin', number]> = [['debut', debut], ['recu', n(s.recu)], ['totalUtilise', total], ['fin', fin]];
  return {
    debut, debutReporte: reporte, recu: n(s.recu), totalUtilise: total, perime: n(s.perime), avarie: n(s.avarie),
    finCalcule, fin, finSaisi, coherent: !finSaisi || fin === finCalcule, negatif: finCalcule < 0,
    vaccines, tauxPerte, alertePerte: tauxPerte !== null && tauxPerte >= SEUIL_PERTE_ALERTE,
    multiplesNonRespectes: m ? aVerifier.filter(([, v]) => v !== 0 && v % m !== 0).map(([k]) => k) : [],
  };
}

/**
 * Stock fin à reporter au mois suivant, ou null s'il n'est pas fiable :
 * valeur saisie (comptage) sinon valeur calculée, mais seulement si le stock début a été saisi (sinon le calcul
 * ignorerait un éventuel report du mois d'avant). Jamais négatif.
 */
export function finPourReport(saisie: Partial<IntrantPev> | null | undefined, seances: SeanceCle[]): number | null {
  if (!saisie) return null;
  if (saisie.finSaisie !== null && saisie.finSaisie !== undefined) return n(saisie.finSaisie);
  if (saisie.debut === null || saisie.debut === undefined) return null;
  const f = n(saisie.debut) + n(saisie.recu) - totalUtilise(saisie, seances) - n(saisie.perime) - n(saisie.avarie);
  return f >= 0 ? f : null;
}

/**
 * Ligne « vide » (rien à envoyer) : aucun champ renseigné et aucune quantité par séance.
 * Reçu / périmé / avarié valent 0 quand ils sont vides (voir normaliserIntrantPev) : 0 = vide pour ces trois-là.
 * Stock début et stock fin compté gardent leur sens avec 0 (« compté à zéro ») : seul null/undefined = vide.
 */
export const ligneIntrantPevVide = (s: Partial<IntrantPev> | null | undefined) =>
  !s || ((['debut', 'finSaisie'] as const).every((k) => s[k] === null || s[k] === undefined)
    && (['recu', 'perime', 'avarie'] as const).every((k) => s[k] === null || s[k] === undefined || s[k] === 0)
    && Object.keys(s.bySeance ?? {}).length === 0);

// ------------------------------------------------------------------ Normalisation (envoi au serveur)
const MAX_QUANTITE = 9_999_999;

/** Entier ≥ 0 : vide / null / undefined / texte non numérique / négatif → 0 ; décimale (« 12,5 ») → arrondie ; plafonné à 9 999 999. */
export function quantiteEntiere(x: unknown): number {
  const v = typeof x === 'string' ? Number(x.replace(/[\s ]/g, '').replace(',', '.')) : Number(x);
  const r = Math.round(v);
  return Number.isFinite(r) && r > 0 ? Math.min(r, MAX_QUANTITE) : 0;
}

/** Comme quantiteEntiere, mais null / undefined / « » restent null (stock début : null = report du mois précédent ; stock fin : null = calculé). */
export function quantiteEntiereOuNull(x: unknown): number | null {
  if (x === null || x === undefined) return null;
  if (typeof x === 'string' && x.trim() === '') return null;
  return quantiteEntiere(x);
}

const ALIAS_CLES_PEV: Record<string, string> = {
  td: 'vat', vattd: 'vat', sd2: 'dil2', sd5: 'dil5', diluant2: 'dil2', diluant5: 'dil5', diluant2ml: 'dil2', diluant5ml: 'dil5',
  sab05ml: 'sab05', sab005ml: 'sab005', sab0: 'sab05', pcv: 'pcv10', rotavirus: 'rota',
  solvantbcg: 'solvbcg', solvantvar: 'solvvar', boitedesecurite: 'boitesecu',
};

/**
 * Identifiant d'un intrant PEV tel que la base et la validation l'attendent : minuscules, sans espace ni tiret
 * (« BCG », « bcg  », « Dil-2 » → « bcg », « dil2 »). Une clé inconnue est renvoyée nettoyée (la validation la refusera clairement).
 */
export function normaliserCleIntrantPev(cle: unknown): string {
  const brut = String(cle ?? '').replace(/[ ​-‍﻿]/g, ' ').trim().toLowerCase();
  if (estCleIntrantPev(brut)) return brut;
  const compact = brut.replace(/[\s_\-.,()/]+/g, '');
  if (estCleIntrantPev(compact)) return compact;
  return ALIAS_CLES_PEV[compact] ?? brut;
}

/**
 * Données d'un intrant PEV prêtes pour la base : reçu / périmé / avarié / quantités par séance vides, null ou undefined → 0
 * (Number(valeur) || 0, entier ≥ 0) ; stock début et stock fin compté gardent null quand ils ne sont pas saisis (report / calcul automatique) ;
 * les champs inconnus (ancienne version de l'application) sont écartés au lieu de faire refuser toute la ligne.
 * Un objet invalide (pas un objet simple) est renvoyé tel quel : la validation le refusera.
 */
export function normaliserIntrantPev(d: unknown): Partial<IntrantPev> {
  if (!d || typeof d !== 'object' || Array.isArray(d)) return d as Partial<IntrantPev>;
  const s = d as Record<string, unknown>;
  const bySeanceBrut = s.bySeance;
  let bySeance: Record<string, number>;
  if (bySeanceBrut === undefined || bySeanceBrut === null) bySeance = {};
  else if (typeof bySeanceBrut !== 'object' || Array.isArray(bySeanceBrut)) return d as Partial<IntrantPev>; // structure inattendue : validation
  else {
    bySeance = {};
    for (const [k, v] of Object.entries(bySeanceBrut as Record<string, unknown>)) {
      const cle = k.replace(/[ ​-‍﻿]/g, ' ').trim();
      if (cle) bySeance[cle] = quantiteEntiere(v);
    }
  }
  return {
    debut: quantiteEntiereOuNull(s.debut), recu: quantiteEntiere(s.recu), perime: quantiteEntiere(s.perime), avarie: quantiteEntiere(s.avarie),
    finSaisie: quantiteEntiereOuNull(s.finSaisie), bySeance,
  };
}

export function validerIntrantPev(cle: string, d: any): { ok: boolean; erreurs: string[] } {
  const erreurs: string[] = [];
  if (!estCleIntrantPev(cle)) return { ok: false, erreurs: ['Produit PEV inconnu.'] };
  if (!d || typeof d !== 'object' || Array.isArray(d)) return { ok: false, erreurs: ['Données invalides.'] };
  const connus = ['debut', 'recu', 'perime', 'avarie', 'finSaisie', 'bySeance'];
  const extra = Object.keys(d).filter((k) => !connus.includes(k));
  if (extra.length) erreurs.push(`Champ inconnu : ${extra.join(', ')}.`);
  for (const k of ['debut', 'recu', 'perime', 'avarie', 'finSaisie']) {
    const v = d[k];
    if (v !== null && v !== undefined && (!Number.isInteger(v) || v < 0 || v > 9_999_999)) erreurs.push(`${k} : entier entre 0 et 9 999 999.`);
  }
  const b = d.bySeance;
  if (b !== undefined) {
    if (!b || typeof b !== 'object' || Array.isArray(b)) erreurs.push('Quantités par séance invalides.');
    else {
      const e = Object.entries(b);
      if (e.length > MAX_SEANCES_INTRANT) erreurs.push(`Au plus ${MAX_SEANCES_INTRANT} séances par produit.`);
      for (const [k, v] of e) if (k.length < 1 || k.length > 60 || !Number.isInteger(v) || (v as number) < 0 || (v as number) > 9_999_999) { erreurs.push('Quantité par séance invalide (entier ≥ 0).'); break; }
    }
  }
  return { ok: erreurs.length === 0, erreurs };
}
